import React, { useState, useEffect, useRef, forwardRef } from 'react';
import {
  Text,
  TextInput,
  TouchableOpacity,
  View,
  ActivityIndicator,
  Platform,
  ScrollView,
} from 'react-native';
import DatePicker from 'react-datepicker';
import { FontAwesome5 } from '@expo/vector-icons';

import { quickAddDiscountCode } from '../../../api/Settings';
import { getBrandsData } from '../../../api/Price';
import BasicModalContainer from '../../../common/components/basicmodal/BasicModalContainer';
import ModalHeader from '../../../common/components/basicmodal/ModalHeader';
import ModalBody from '../../../common/components/basicmodal/ModalBody';
import ModalFooter from '../../../common/components/basicmodal/ModalFooter';
import { msgStr } from '../../../common/constants/Message';
import { useAlertModal } from '../../../common/hooks';
import { Picker } from '@react-native-picker/picker';

import { discountCodeModalstyles } from './styles/DiscountCodeModalStyle';
import { format } from 'date-fns';
import { exclusionStyle } from './styles/ExclusionStyle';
import { BOHTlbCheckbox } from '../../../common/components/bohtoolbar';
import { TextMediumSize, TextSmallSize } from '../../../common/constants/Fonts';

// CODE GENERATION UTILITIES
const generateSecureCode = (prefix = '', length = 6) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  
  return prefix.toUpperCase() + Array.from(array, byte => chars[byte % chars.length]).join('');
};

const generateCodesMemoryEfficient = function* (count, prefix = '', existingCodes = []) {
  const blacklist = new Set(existingCodes.map(code => code.toUpperCase()));
  const generated = new Set();
  let yielded = 0;
  let attempts = 0;
  const maxAttempts = count * 15;
  
  while (yielded < count && attempts < maxAttempts) {
    const code = generateSecureCode(prefix, 6);
    
    if (!blacklist.has(code) && !generated.has(code)) {
      generated.add(code);
      yield code;
      yielded++;
    }
    
    attempts++;
  }
  
  if (yielded < count) {
    console.warn(`Generated ${yielded}/${count} codes after ${attempts} attempts`);
  }
};

const AddDiscountCodeModal = ({
  isModalVisible,
  DiscountCode,
  BlackList,
  setUpdateDiscountCodeTrigger,
  closeModal,
}) => {
  const { showAlert } = useAlertModal();
  const [errors, setErrors] = useState({
    count: '',
    codePrefix: '',
    amount: '',
    dates: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const [types, setTypes] = useState([
    { id: 1, type: 'Percentage' },
    { id: 2, type: 'Flat fee' },
  ]);

  const [CountsTxt, setCountsTxt] = useState('');
  const [CodePrefixTxt, setCodePrefixTxt] = useState('');
  const [Type, setType] = useState(0);
  const [AmountTxt, setAmountTxt] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [savedCodes, setSavedCodes] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(0);
  const [oneTimeUse, setOneTimeUse] = useState(false);
  const [brandData, setBrandData] = useState([]);

  useEffect(() => {
    setCodePrefixTxt('');
    setType(1);
    setAmountTxt('');
    setStartDate(null);
    setEndDate(null);
    setSavedCodes([]);
    setOneTimeUse(false);
    setBrandData([]);
    setSelectedBrand(0);
    setErrors({
      count: '',
      codePrefix: '',
      amount: '',
      dates: '',
    });
    // get brands
    getBrands();
  }, [isModalVisible]);

  const getBrands = () => {
    getBrandsData((jsonRes, status, error) => {
      switch (status) {
        case 200:
          const sortedData = jsonRes
            .filter((item: any) => item.brand != null)
            .sort((a: any, b: any) => a.brand.localeCompare(b.brand));

          setBrandData(sortedData || []);
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  };

  // Validate form fields
  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      count: '',
      codePrefix: '',
      amount: '',
      dates: '',
    };

    // Validate count
    if (!CountsTxt.trim()) {
      newErrors.count = msgStr('emptyField');
      isValid = false;
    } else if (isNaN(parseInt(CountsTxt)) || parseInt(CountsTxt) <= 0) {
      newErrors.count = 'Please enter a valid positive number';
      isValid = false;
    }

    // Validate code prefix
    if (!CodePrefixTxt.trim()) {
      newErrors.codePrefix = msgStr('emptyField');
      isValid = false;
    } else if (CodePrefixTxt.trim().length < 2) {
      newErrors.codePrefix = 'Prefix must be at least 2 characters';
      isValid = false;
    }

    // Validate amount
    if (!AmountTxt.trim()) {
      newErrors.amount = msgStr('emptyField');
      isValid = false;
    } else if (isNaN(parseFloat(AmountTxt)) || parseFloat(AmountTxt) <= 0) {
      newErrors.amount = 'Please enter a valid positive amount';
      isValid = false;
    } else if (Type === 1 && parseFloat(AmountTxt) > 100) {
      newErrors.amount = 'Percentage cannot exceed 100%';
      isValid = false;
    }

    // Validate dates if both are provided
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      newErrors.dates = 'End date must be after start date';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const AddButtonHandler = async () => {
    if (!validateForm()) {
      return;
    }

    setIsGenerating(true);
    
    try {
      const count = parseInt(CountsTxt, 10) || 0;
      
      // Combine BlackList and existing saved codes
      const existingCodes = [
        ...(BlackList || []).map(item => item.code || item),
        ...savedCodes.map(item => item.code)
      ];
      
      // Generate codes using memory-efficient method
      const generatedCodes = [...generateCodesMemoryEfficient(count, CodePrefixTxt, existingCodes)];
      
      // Check if we got all requested codes
      if (generatedCodes.length < count) {
        showAlert('warning', `Warning: Could only generate ${generatedCodes.length} out of ${count} unique codes requested.`);
      }
      
      // Convert to your data structure
      const newCodes = generatedCodes.map(code => ({
        code: code,
        amount: AmountTxt || '0',
        startDate: startDate,
        endDate: endDate,
        type: Type,
        brand: selectedBrand,
        is_one_time_use: oneTimeUse,
      }));

      // Add to saved codes
      setSavedCodes([...savedCodes, ...newCodes]);

      // Reset fields
      setCountsTxt('');
      setCodePrefixTxt('');
      setAmountTxt('');
      setStartDate(null);
      setEndDate(null);
      setType(1);
      setSelectedBrand(0);
      setOneTimeUse(false);
      setErrors({
        count: '',
        codePrefix: '',
        amount: '',
        dates: '',
      });

    } catch (error) {
      console.error('Error generating codes:', error);
      showAlert('error', 'Failed to generate codes. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Delete individual code
  const deleteCode = (index) => {
    const updatedCodes = savedCodes.filter((_, i) => i !== index);
    setSavedCodes(updatedCodes);
  };

  // Clear all codes
  const clearAllCodes = () => {
    setSavedCodes([]);
  };

  // Save all codes to API
  const SaveButtonHandler = () => {
    if (savedCodes.length === 0) {
      showAlert('error', 'No discount codes to save. Please add codes first.');
      return;
    }

    // Clear any previous validation messages
    setErrors({
      count: '',
      codePrefix: '',
      amount: '',
      dates: '',
    });

    setIsLoading(true);

    const payload: any = {
      codes: savedCodes.map((code) => ({
        code: code.code,
        type: code.type,
        amount: code.amount,
        valid_start_date: code.startDate,
        valid_end_date: code.endDate,
        brand_id: code.brand,
        is_one_time_use: code.is_one_time_use,
      })),
    };

    const handleResponse = (jsonRes, status) => {
      switch (status) {
        case 201:
          showAlert('success', jsonRes.message);
          setUpdateDiscountCodeTrigger(true);
          closeModal();
          break;
        case 409:
          showAlert('error', jsonRes.error);
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          closeModal();
          break;
      }
      setIsLoading(false);
    };

    quickAddDiscountCode(payload, (jsonRes, status) => {
      handleResponse(jsonRes, status);
    });
  };

  const closeModalhandler = () => {
    closeModal();
  };

  const checkInput = () => {
    validateForm();
  };

  const CustomInput = forwardRef(({ value, onChange, onClick }, ref) => (
    <input
      onClick={onClick}
      onChange={onChange}
      ref={ref}
      style={styles.input}
      value={value}
    ></input>
  ));

  const renderDatePicker = (selectedDate, onChangeHandler) => {
    return (
      <View style={{ paddingRight: 22 }}>
        <DatePicker
          selected={selectedDate}
          onChange={(date) => onChangeHandler(date)}
          customInput={<CustomInput />}
          peekNextMonth
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          timeInputLabel="Time:"
          dateFormat="MM/dd/yyyy hh:mm aa"
          showTimeSelect
        />
      </View>
    );
  };

  return isModalVisible ? (
    <View style={{ position: 'absolute', width: '100%', height: '100%' }}>
      <BasicModalContainer>
        <ModalHeader
          label={'Quick Add'}
          closeModal={() => {
            closeModalhandler();
          }}
        />
        <ModalBody style={{ zIndex: 10 }}>
          <View style={{ flexDirection: 'row', zIndex: 10 }}>
            <View style={{ flex: 1, marginRight: 20 }}>
              <Text style={styles.label}>Code prefix</Text>
              <TextInput
                style={[styles.input, errors.codePrefix ? styles.inputError : null]}
                placeholder="Code prefix"
                value={CodePrefixTxt}
                onChangeText={(text) => {
                  setCodePrefixTxt(text);
                  if (errors.codePrefix) {
                    setErrors({ ...errors, codePrefix: '' });
                  }
                }}
                placeholderTextColor="#ccc"
                onBlur={checkInput}
              />
              {errors.codePrefix ? <Text style={styles.message}>{errors.codePrefix}</Text> : null}

              <Text style={styles.label}>Type</Text>
              <Picker style={styles.select} selectedValue={Type} onValueChange={setType}>
                {types.length > 0 &&
                  types.map((type, index) => {
                    return <Picker.Item key={index} label={type.type} value={type.id} />;
                  })}
              </Picker>

              <Text style={styles.label}>Start date</Text>
              {Platform.OS == 'web' && renderDatePicker(startDate, setStartDate)}

              <Text style={styles.label}>Brand</Text>
              <Picker style={styles.select} selectedValue={selectedBrand} onValueChange={setSelectedBrand}>
                <Picker.Item label="" value={0} />
                {brandData.length > 0 &&
                  brandData.map((brand, index) => {
                    return <Picker.Item key={index} label={brand.brand} value={brand.id} />;
                  })}
              </Picker>
            </View>
            <View style={{ flex: 1, marginRight: 0 }}>
              <Text style={styles.label}>How many do you want to create?</Text>
              <TextInput
                style={[styles.input, errors.count ? styles.inputError : null]}
                placeholder="How many? (e.g., 50, 100, 1000)"
                value={CountsTxt}
                onChangeText={(text) => {
                  setCountsTxt(text);
                  if (errors.count) {
                    setErrors({ ...errors, count: '' });
                  }
                }}
                keyboardType="numeric"
                placeholderTextColor="#ccc"
                onBlur={checkInput}
              />
              {errors.count ? <Text style={styles.message}>{errors.count}</Text> : null}

              <Text style={styles.label}>Amount</Text>
              <TextInput
                style={[styles.input, errors.amount ? styles.inputError : null]}
                placeholder="Amount"
                value={AmountTxt}
                onChangeText={(text) => {
                  setAmountTxt(text);
                  if (errors.amount) {
                    setErrors({ ...errors, amount: '' });
                  }
                }}
                keyboardType="numeric"
                placeholderTextColor="#ccc"
                onBlur={checkInput}
              />
              {errors.amount ? <Text style={styles.message}>{errors.amount}</Text> : null}

              <Text style={styles.label}>End date</Text>
              {Platform.OS == 'web' && renderDatePicker(endDate, setEndDate)}
              {errors.dates ? <Text style={styles.message}>{errors.dates}</Text> : null}

              <BOHTlbCheckbox
                style={{marginTop: 22, marginLeft: 0}}
                label={'One time use'}
                labelStyle={{fontSize: TextSmallSize, color: '#555'}}
                CheckboxProps={{value:oneTimeUse}}
                onPress={()=>{
                  setOneTimeUse(!oneTimeUse);
                  }}
                />
            </View>
          </View>

          {/* Added Discount Codes Table */}
          <View style={{ flex: 1, marginTop: 20 }}>
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 10,
              }}
            >
              <Text style={{ fontSize: 16, fontWeight: 'bold' }}>
                Codes {savedCodes.length > 0 && `(${savedCodes.length})`}
              </Text>
              <View style={{ flexDirection: 'row', gap: 10 }}>
                {savedCodes.length > 0 && (
                  <TouchableOpacity
                    onPress={clearAllCodes}
                  >
                    <Text style={styles.addButton}>Clear All</Text>
                  </TouchableOpacity>
                )}
                <TouchableOpacity
                  onPress={AddButtonHandler}
                  style={[
                    (!CountsTxt.trim() || !CodePrefixTxt.trim() || !AmountTxt.trim() || isGenerating)
                      ? { opacity: 0.5 }
                      : {},
                  ]}
                  disabled={!CountsTxt.trim() || !CodePrefixTxt.trim() || !AmountTxt.trim() || isGenerating}
                >
                  <Text style={styles.addButton}>
                    {isGenerating ? 'Generating...' : 'Add'}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
            <View style={[styles.tableContainer]}>
              <View style={styles.tableHeader}>
                <Text style={[styles.columnHeader, { width: 120 }]}>{'Code'}</Text>
                <Text style={[styles.columnHeader, { width: 120 }]}>{'Type'}</Text>
                <Text style={[styles.columnHeader, { width: 80 }]}>{'Amount'}</Text>
                <Text style={[styles.columnHeader, { width: 120 }]}>{'Brand'}</Text>
                <Text style={[styles.columnHeader, { width: 120 }]}>{'Start Date'}</Text>
                <Text style={[styles.columnHeader, { width: 120 }]}>{'End Date'}</Text>
                <Text style={[styles.columnHeader, { width: 100 }]}>{'One Time'}</Text>
                <Text style={[styles.columnHeader, { width: 70 }]}>{'Actions'}</Text>
              </View>
              <ScrollView style={{ minHeight: 30, maxHeight: 200 }}>
                {savedCodes.length > 0 ? (
                  savedCodes.map((code, index) => (
                    <View key={index} style={styles.tableRow}>
                      <View style={[styles.cell, { width: 120 }]}>
                        <Text>{code.code}</Text>
                      </View>
                      <View style={[styles.cell, { width: 120 }]}>
                        <Text>
                          {parseInt(code.type) === 1
                            ? 'Percentage'
                            : parseInt(code.type) === 2
                              ? 'Flat fee'
                              : '-'}
                        </Text>
                      </View>
                      <View style={[styles.cell, { width: 80 }]}>
                        <Text>
                          {parseInt(code.type) === 1 ? `${code.amount}%` : `$${code.amount}`}
                        </Text>
                      </View>
                      <View style={[styles.cell, { width: 120 }]}>
                        <Text>
                          {code.brand && parseInt(code.brand) !== 0 
                            ? brandData.find(b => parseInt(b.id) === parseInt(code.brand))?.brand || '-'
                            : '-'}
                        </Text>
                      </View>
                      <View style={[styles.cell, { width: 120 }]}>
                        <Text>
                          {code.startDate
                            ? format(new Date(code.startDate), 'MM/dd/yyyy hh:mm aa')
                            : '-'}
                        </Text>
                      </View>
                      <View style={[styles.cell, { width: 120 }]}>
                        <Text>
                          {code.endDate
                            ? format(new Date(code.endDate), 'MM/dd/yyyy hh:mm aa')
                            : '-'}
                        </Text>
                      </View>
                      <View style={[styles.cell, { width: 100, alignItems: 'center' }]}>
                        <Text>{code.is_one_time_use ? 'Yes' : 'No'}</Text>
                      </View>
                      <View style={[styles.cell, { width: 70, alignItems: 'center' }]}>
                        <TouchableOpacity
                          onPress={() => {
                            deleteCode(index);
                          }}
                        >
                          <FontAwesome5 size={TextMediumSize} name="times" color="black" />
                        </TouchableOpacity>
                      </View>
                    </View>
                  ))
                ) : (
                  <View style={{ alignItems: 'center', paddingTop: 10 }}>
                    <Text>No codes added yet. Click "Add" to add codes.</Text>
                  </View>
                )}
              </ScrollView>
            </View>
          </View>
        </ModalBody>
        <ModalFooter>
          <View style={{ flexDirection: 'row' }}>
            <TouchableOpacity onPress={SaveButtonHandler} disabled={isLoading}>
              <Text style={styles.addButton}>
                {isLoading ? 'Saving...' : 'Save'}
              </Text>
            </TouchableOpacity>
          </View>
        </ModalFooter>
      </BasicModalContainer>

      {(isLoading || isGenerating) && (
        <View style={styles.overlay}>
          <ActivityIndicator size="large" color="#0000ff" />
          <Text style={{ marginTop: 10, color: '#000' }}>
            {isGenerating ? 'Generating codes...' : 'Saving codes...'}
          </Text>
        </View>
      )}
    </View>
  ) : null;
};

const styles = {
  ...discountCodeModalstyles,
  ...exclusionStyle,
  inputError: {
    borderColor: 'red',
    borderWidth: 1,
  },
};

export default AddDiscountCodeModal;